import { styled } from "@mui/material"
import type { ReactNode } from "react"
import type { IsoDate, SourceName } from "../../../../types"
import { isDefined, pluralize } from "../../../../utils"
import {
  AsideSectionContentTitle,
  AsideSectionEmpty,
  AsideSectionSummaryInfo,
  AsideSectionSummaryMainCard,
  AsideSectionSummaryMainEvolution,
  AsideSectionSummarySecondaryCard,
  formatPrice,
  noDecimalsFormatOptions,
} from "../../../dataDisplay"
import {
  AsideSection,
  AsideSectionContentArea,
  AsideSectionSummaryLayout,
} from "../../AsideSection"
import { StockCard, type StockCardProps } from "../../StockCard"

interface StocksSectionProps {
  stock: {
    volume?: number | null
    nbDaysVolume?: number | null
    valueHt?: number | null
    valueTtc?: number | null
    nbDaysValueTtc?: number | null
  }
  stockPerVatRate: Omit<StockCardProps, "index">[]
  periodEndDate: IsoDate
  sources: SourceName[]
  title?: ReactNode
  titleChildren?: ReactNode
}

export const StocksSection = ({
  stock,
  stockPerVatRate,
  periodEndDate,
  sources,
  title = "Répartition par taux de TVA",
  titleChildren,
}: StocksSectionProps) => {
  return (
    <AsideSection data-testid="stocks-section">
      {/* TITLE */}
      <AsideSectionContentTitle title={title}>
        {titleChildren}
      </AsideSectionContentTitle>

      {/* ASIDE */}
      <AsideSectionSummaryLayout>
        <AsideSectionSummaryMainCard
          title="STOCKS"
          subTitle="Valeur Prix d'achat HT net"
        >
          <AsideSectionSummaryMainEvolution
            kpi={
              isDefined(stock?.valueHt)
                ? formatPrice(stock.valueHt, noDecimalsFormatOptions)
                : "-"
            }
          />
        </AsideSectionSummaryMainCard>
        <AsideSectionSummarySecondaryCard
          title="Nb de jours de stock"
          kpi={
            isDefined(stock?.nbDaysValueTtc)
              ? `${stock.nbDaysValueTtc} ${pluralize(stock.nbDaysValueTtc, "jour", "jours")}`
              : "-"
          }
          layout="basic"
        />
        <AsideSectionSummaryInfo period={periodEndDate} sources={sources} />
      </AsideSectionSummaryLayout>

      {/* CONTENT */}
      {stockPerVatRate?.length > 0 ? (
        <GridLayout>
          {stockPerVatRate.map((stock, i) => (
            <StockCard key={stock.vatRate} index={i} {...stock} />
          ))}
        </GridLayout>
      ) : (
        <AsideSectionEmpty />
      )}
    </AsideSection>
  )
}

const GridLayout = styled(AsideSectionContentArea)(({ theme }) => ({
  display: "grid",
  gap: theme.spacing(3),
  gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))",
  justifyContent: "center",
  justifyItems: "center",
  rowGap: theme.spacing(2),
  width: "100%",
}))
